import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Plus, Pencil, Trash2, Tag, Loader2 } from 'lucide-react';
import { categoriasDespesaService, CategoriaDeDespesa } from '../../services/categoriasDespesa';
import { useConfirm } from '../../components/ConfirmDialog';

/**
 * O catálogo de categorias de despesa da empresa.
 *
 * Eram doze, fixas no código, iguais para todo mundo. Frota de caminhão gasta
 * com borracharia e balança; locadora, com higienização. Quem não achava a sua
 * lançava tudo em "Outros", e o relatório de custo virava uma coluna gigante
 * sem significado nenhum.
 *
 * Duas coisas a tela precisa deixar claras, porque são contra-intuitivas:
 *
 * Renomear vale para o histórico inteiro. Quem renomeia está corrigindo o nome
 * de uma coisa que sempre foi a mesma, e não criando outra.
 *
 * Excluir não apaga lançamento. As despesas já lançadas continuam somando no
 * relatório do ano passado; o que acaba é a possibilidade de lançar de novo
 * naquela categoria.
 */
const CategoriasDespesa: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [criando, setCriando] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [editando, setEditando] = useState<CategoriaDeDespesa | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['categorias-despesa'],
    queryFn: categoriasDespesaService.listar,
  });

  const categorias = data?.data ?? [];

  const aoFalhar = (erro: any) => {
    const erros = erro?.response?.data?.errors;
    const primeiro = erros ? (Object.values(erros)[0] as string[])?.[0] : null;
    toast.error(primeiro ?? erro?.response?.data?.message ?? 'Não foi possível salvar.');
  };

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['categorias-despesa'] });

  const criar = useMutation({
    mutationFn: (nome: string) => categoriasDespesaService.criar(nome),
    onSuccess: () => {
      toast.success('Categoria criada.');
      setNomeNovo('');
      setCriando(false);
      recarregar();
    },
    onError: aoFalhar,
  });

  const renomear = useMutation({
    mutationFn: ({ id, nome }: { id: number; nome: string }) =>
      categoriasDespesaService.renomear(id, nome),
    onSuccess: () => {
      toast.success('Categoria atualizada. O histórico acompanha o novo nome.');
      setEditando(null);
      recarregar();
    },
    onError: aoFalhar,
  });

  const excluir = useMutation({
    mutationFn: (id: number) => categoriasDespesaService.excluir(id),
    onSuccess: () => {
      toast.success('Categoria excluída. Os lançamentos antigos continuam no histórico.');
      recarregar();
    },
    onError: aoFalhar,
  });

  const pedirExclusao = async (categoria: CategoriaDeDespesa) => {
    // O texto diz o que ACONTECE, e não "tem certeza?". Quem exclui precisa
    // saber que o relatório do ano passado continua de pé.
    const confirmado = await confirm({
      title: `Excluir "${categoria.nome}"?`,
      message:
        'As despesas já lançadas nesta categoria continuam no histórico e nos relatórios. '
        + 'O que muda é que ela deixa de aparecer para novos lançamentos.',
      confirmLabel: 'Excluir',
      variant: 'danger',
    });

    if (confirmado) excluir.mutate(categoria.id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/despesas')}
          className="p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100"
          aria-label="Voltar para despesas"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias de despesa</h1>
          <p className="text-sm text-gray-500">
            A lista que aparece ao lançar uma despesa, aqui e no portal do motorista.
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {categorias.length} categoria(s)
          </h2>

          {!criando && (
            <button
              type="button"
              onClick={() => setCriando(true)}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Nova categoria
            </button>
          )}
        </div>

        {criando && (
          <form
            className="px-6 py-4 border-b border-gray-200 bg-blue-50 flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (nomeNovo.trim()) criar.mutate(nomeNovo.trim());
            }}
          >
            <div className="flex-1 min-w-[14rem]">
              <label htmlFor="nome-novo" className="block text-sm font-medium text-gray-700 mb-1">
                Nome da categoria
              </label>
              <input
                id="nome-novo"
                autoFocus
                value={nomeNovo}
                onChange={(e) => setNomeNovo(e.target.value)}
                maxLength={60}
                placeholder="Ex.: Borracharia"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={criar.isPending || !nomeNovo.trim()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {criar.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Salvar
            </button>
            <button
              type="button"
              onClick={() => {
                setCriando(false);
                setNomeNovo('');
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
          </form>
        )}

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        )}

        <ul className="divide-y divide-gray-100">
          {categorias.map((categoria) => (
            <li key={categoria.id} className="px-6 py-3">
              {editando?.id === categoria.id ? (
                <form
                  className="flex flex-wrap items-end gap-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (nomeEditado.trim()) {
                      renomear.mutate({ id: categoria.id, nome: nomeEditado.trim() });
                    }
                  }}
                >
                  <div className="flex-1 min-w-[14rem]">
                    <label
                      htmlFor={`nome-${categoria.id}`}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Novo nome
                    </label>
                    <input
                      id={`nome-${categoria.id}`}
                      autoFocus
                      value={nomeEditado}
                      onChange={(e) => setNomeEditado(e.target.value)}
                      maxLength={60}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      O novo nome vale também para as despesas já lançadas nesta categoria.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={renomear.isPending}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditando(null)}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </form>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2 min-w-0">
                    <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-900 truncate">{categoria.nome}</span>
                  </span>

                  <span className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setEditando(categoria);
                        setNomeEditado(categoria.nome);
                      }}
                      className="p-2 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                      aria-label={`Renomear ${categoria.nome}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => pedirExclusao(categoria)}
                      className="p-2 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                      aria-label={`Excluir ${categoria.nome}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>

        {!isLoading && categorias.length === 0 && (
          <p className="px-6 py-10 text-center text-sm text-gray-500">
            Nenhuma categoria cadastrada. Crie a primeira para começar a lançar despesas.
          </p>
        )}
      </div>
    </div>
  );
};

export default CategoriasDespesa;
